import { useDisclosure } from '@chakra-ui/react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { GenericFilterSortDropdown } from '$/components/common/GenericFilterSortDropdown';
import { FilterActions } from '$/components/core/Collection/CollectionFilter/components/FilterActions';
import { MultiSelectionMenuItem } from '$/components/core/Collection/CollectionFilter/components/MultiSelectionMenuItem';
import { SingleMenuItem } from '$/components/core/Collection/CollectionFilter/components/SingleMenuItem';
import {
  HARMONY_FACETS,
  useFilteredHarmonies,
} from '$/pages/DashboardPages/pages/DesignGuide/hooks/useFilteredHarmonies';
import {
  useDesignGuideStore,
  type HarmonyFacet,
} from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { trackHarmonyFilter } from '$/pages/InspirationEditorPage/lib/harmonyUtils';
import { capitalizeSentence } from '$/utils/stringUtils';

export interface FacetDropdownProps {
  facet: HarmonyFacet;
  label: string;
  isDisabled?: boolean;
}

export const FacetDropdown: FC<FacetDropdownProps> = ({
  facet,
  label,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeChanges, setActiveChanges] = useState(false);
  const { facetCounts: counts } = useFilteredHarmonies();

  const isActive = useDesignGuideStore.useIsActive();
  const toggleSingleFilter = useDesignGuideStore.useToggleSingleFilter();
  const toggleMultiFilter = useDesignGuideStore.useToggleMultiFilter();
  const reset = useDesignGuideStore.useReset();
  const selection = useDesignGuideStore.useSelection();
  const scene = (selection.scene ?? 'exterior') as 'exterior' | 'interior';

  const isMulti = HARMONY_FACETS[facet].mode === 'multi';
  const options = Object.entries(counts[facet]).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const hasActiveFilter = selection[facet] !== undefined;

  return (
    <GenericFilterSortDropdown
      title={label}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={() => {
        onClose();
        setActiveChanges(false);
      }}
      flexShrink='0'
      dataTestId={facet}
      isDisabled={isDisabled}
    >
      {options.map(([value, count]) => {
        if (!count) return null;

        if (isMulti) {
          return (
            <MultiSelectionMenuItem
              key={value}
              id={value}
              name={t(`dashboard.designGuide.facets.${facet}.${value}`, {
                defaultValue: capitalizeSentence(value),
              })}
              isChecked={isActive(facet, value)}
              amount={count || undefined}
              onChange={() => {
                setActiveChanges(true);
                if (!isActive(facet, value)) {
                  trackHarmonyFilter(facet, value, scene);
                }
                toggleMultiFilter(facet, value);
              }}
            />
          );
        }

        return (
          <SingleMenuItem
            key={value}
            name={t(`dashboard.designGuide.facets.${facet}.${value}`, {
              defaultValue: capitalizeSentence(value),
            })}
            isActive={isActive(facet, value)}
            amount={count || undefined}
            w={64}
            onClick={() => {
              if (!isActive(facet, value)) {
                trackHarmonyFilter(facet, value, scene);
              }
              toggleSingleFilter(facet, value);
              onClose();
            }}
            data-testid={`${facet}-${value}`}
          />
        );
      })}

      {isMulti && (
        <FilterActions
          anyFilterActive={hasActiveFilter}
          activeChanges={activeChanges}
          onSubmit={() => {
            onClose();
            setActiveChanges(false);
          }}
          onReset={() => {
            reset(facet);
            onClose();
            setActiveChanges(false);
          }}
          px='3'
          py='5'
        />
      )}
    </GenericFilterSortDropdown>
  );
};
